import {formatInTimeZone} from 'date-fns-tz';

import {FulfillmentDetailStrategy, type JumpBackInFulfillmentDetailFragment} from '@/graphql/types';
import {DateTimeFormats} from '@/utils/dateFormat';

export type JumpBackInFulfillmentDetail = JumpBackInFulfillmentDetailFragment;
export type JumpBackInCart = JumpBackInFulfillmentDetail['consumerCarts'][number];

export const locationLabelKey = (strategy: FulfillmentDetailStrategy): string =>
  strategy === FulfillmentDetailStrategy.Takeout
    ? 'jumpBackInHomepage.takeoutFrom'
    : 'jumpBackInHomepage.deliveryTo';

export type JumpBackInCardData = {
  fulfillmentDetail: JumpBackInFulfillmentDetail;
  cart: JumpBackInCart;
};

export const MAX_JUMP_BACK_IN_CARDS = 2;
const RECENT_CART_WINDOW_DAYS = 14;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const isWithinDays = (isoDate: string, days: number, now: Date): boolean => {
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return false;
  const diffMs = now.getTime() - then;

  return diffMs >= 0 && diffMs <= days * MS_PER_DAY;
};

const isCatererAvailable = (cart: JumpBackInCart): boolean =>
  cart.caterer.live && !cart.caterer.activePauses?.length;

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const resolveTimeZone = (timeZoneIdentifier: string) =>
  timeZoneIdentifier.length > 0 ? timeZoneIdentifier : localTimeZone;

// `eventAt` is only set when the fulfillment detail has both a date and a time, so a date-only
// event has to fall back to `eventOn` — which stays current for the whole of that day in the
// event's own timezone rather than the viewer's. Fulfillment details with no date at all are
// never past.
const isEventPast = (fulfillmentDetail: JumpBackInFulfillmentDetail, now: Date): boolean => {
  const {address, eventAt, eventOn} = fulfillmentDetail;

  if (eventAt) return new Date(eventAt).getTime() < now.getTime();
  if (eventOn) {
    const timeZone = resolveTimeZone(address.timeZoneIdentifier);

    return eventOn < formatInTimeZone(now, timeZone, DateTimeFormats.IsoDateFormat);
  }

  return false;
};

// A cart resolves to a card when its caterer is live/unpaused and either the fulfillment detail
// has an event date (past ones are dropped before we get here, so any remaining date is upcoming)
// or — when it has no date — the cart was updated within the last 14 days.
const isCartResumable = (
  fulfillmentDetail: JumpBackInFulfillmentDetail,
  cart: JumpBackInCart,
  now: Date,
): boolean => {
  if (!isCatererAvailable(cart)) return false;
  if (fulfillmentDetail.eventAt || fulfillmentDetail.eventOn) return true;

  return isWithinDays(cart.updatedAt, RECENT_CART_WINDOW_DAYS, now);
};

const byUpdatedAtDesc = (a: JumpBackInCardData, b: JumpBackInCardData): number =>
  new Date(b.cart.updatedAt).getTime() - new Date(a.cart.updatedAt).getTime();

export const buildJumpBackInCards = (
  fulfillmentDetails: readonly JumpBackInFulfillmentDetail[],
  now: Date = new Date(),
): JumpBackInCardData[] =>
  fulfillmentDetails
    .filter(fulfillmentDetail => !isEventPast(fulfillmentDetail, now))
    .flatMap(fulfillmentDetail =>
      fulfillmentDetail.consumerCarts
        .filter(cart => isCartResumable(fulfillmentDetail, cart, now))
        .map(cart => ({fulfillmentDetail, cart})),
    )
    .sort(byUpdatedAtDesc)
    .slice(0, MAX_JUMP_BACK_IN_CARDS);
